import React, { useCallback } from 'react';
import { Stack, Box } from '@mui/material';
import { Typography } from '@ocx/ui';

import { LoyaltyCurrencyChange } from '@ocx/feat-loyalty-currency';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OfferProgressOverTime } from '@ocx/ui-offers';
import {
  DynamicCard,
  DynamicCardAccessory,
  DynamicCardContent,
  DynamicCardMedia,
  DynamicCardSlot,
} from '../DynamicCard';
import { OfferExpirationChip, OfferLabel, AvailableAtLocations } from './components';
import { SurgeonWarning } from '../SurgeonWarning/SurgeonWarning';
import { OfferTitle } from './components/OfferTitle';
import { OfferCardProps } from './offer-card.types';
import { SizingMultiplier } from '../../theme/constants';
import { useImpressionTracking } from '../../modules/offers/use-impression-tracking.hook';

export const OfferCard = React.forwardRef<HTMLDivElement, OfferCardProps>((props: OfferCardProps, ref) => {
  const {
    variant = 'carousel',
    loading = false,
    disabled,
    impressionTrackingCtx,
    expiresIn,
    rewardExpiresIn,
    marketingContent: { title, shortDescription, imageSrc, surgeonGeneralsWarning },
    label,
    pointsDiff,
    progress,
    onClick,
    accessoryType = 'none',
    onAccessoryButtonClick,
    accessoryButtonDisabled,
    pointsStrategy,
    availableAtLocations,
  } = props;

  const { ref: trackingRef } = useImpressionTracking(impressionTrackingCtx);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      trackingRef(node);
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        const mutableRef = ref as React.MutableRefObject<HTMLDivElement | null>;
        mutableRef.current = node;
      }
    },
    [trackingRef, ref],
  );

  return (
    <DynamicCard
      ref={setRefs}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
      sx={{
        border: variant === 'sheet' ? (theme) => `1px ${theme.palette.divider} solid` : undefined,
      }}>
      {surgeonGeneralsWarning && (
        <Box
          sx={(theme) => ({
            m:
              variant !== 'banner'
                ? theme.spacing(SizingMultiplier.md, SizingMultiplier.md, -SizingMultiplier.sm)
                : SizingMultiplier.sm,
          })}>
          <SurgeonWarning message={surgeonGeneralsWarning} square={variant === 'banner'} />
        </Box>
      )}
      <Box position="relative">
        {variant === 'banner' && imageSrc && <DynamicCardMedia src={imageSrc} alt={title} variant="banner" />}
        {variant === 'banner' && expiresIn && (
          <DynamicCardSlot positionHorizontal="right">
            <OfferExpirationChip time={expiresIn} />
          </DynamicCardSlot>
        )}
        <DynamicCardContent>
          {variant !== 'banner' && imageSrc && <DynamicCardMedia src={imageSrc} alt={title} />}
          <Stack flex={1} alignItems="flex-start" justifyContent="center" spacing={SizingMultiplier.xs}>
            {variant !== 'banner' && label && <OfferLabel label={label} pointsStrategy={pointsStrategy} />}
            <OfferTitle variant={variant}>{title}</OfferTitle>
            {shortDescription && variant !== 'carousel' && (
              <Typography variant="caption" component="p" color="textSecondary">
                {shortDescription}
              </Typography>
            )}
            {progress?.canShowMemberProgress && (
              <OfferProgressOverTime
                progress={progress.memberProgress}
                variant={progress.progressVariant}
                formatVariant={progress.formatVariant}
              />
            )}
            {(pointsDiff || availableAtLocations) && (
              <Stack direction="row" spacing={SizingMultiplier.sm} alignItems="center">
                {pointsDiff && <LoyaltyCurrencyChange points={pointsDiff.diff} />}
                {availableAtLocations && <AvailableAtLocations count={availableAtLocations.length} />}
              </Stack>
            )}
            {variant === 'banner' && label && <OfferLabel label={label} pointsStrategy={pointsStrategy} />}
            {variant === 'carousel' && rewardExpiresIn && (
              <Typography variant="body2" component="span" color="text.secondary">
                {rewardExpiresIn}
              </Typography>
            )}
          </Stack>
          <DynamicCardAccessory
            type={accessoryType}
            onClick={onAccessoryButtonClick}
            disabled={accessoryButtonDisabled}
          />
        </DynamicCardContent>
      </Box>
    </DynamicCard>
  );
});
